import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Badge } from "../../components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";
import { 
  Settings, CreditCard, Bell, Key, CheckCircle2, XCircle, 
  RefreshCw, Eye, EyeOff, Calendar, Mail, Send, Clock, Gift, Download, FileDown, Shield, DollarSign, Percent
} from "lucide-react";
import { NestIcon } from "../../components/NestIcon";
import { AgentLayout } from "./AgentDashboard";
import { API } from "../../App";
import axios from "axios";
import { toast } from "sonner";

export default function AgentSettings() {
  const [loading, setLoading] = useState(true);
  const [settings, setSettings] = useState(null);
  const [testing, setTesting] = useState(false);
  const [saving, setSaving] = useState(false);
  
  // Stripe form state
  const [stripeKey, setStripeKey] = useState("");
  const [webhookSecret, setWebhookSecret] = useState("");
  const [subscriptionMode, setSubscriptionMode] = useState(false);
  const [showStripeKey, setShowStripeKey] = useState(false);
  const [showWebhookSecret, setShowWebhookSecret] = useState(false);
  
  // Cron settings state
  const [renewalEnabled, setRenewalEnabled] = useState(true);
  const [reminderDays, setReminderDays] = useState("30,14,7,1");
  
  // Password change state
  const [passwordForm, setPasswordForm] = useState({
    current_password: "",
    new_password: "",
    confirm_password: ""
  });

  // 2FA state
  const [twoFactorEnabled, setTwoFactorEnabled] = useState(false);
  const [enabling2FA, setEnabling2FA] = useState(false);
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCodeData, setQrCodeData] = useState(null);
  const [totpSecret, setTotpSecret] = useState("");
  const [verifyCode, setVerifyCode] = useState("");
  const [verifying2FA, setVerifying2FA] = useState(false);
  const [disableCode, setDisableCode] = useState("");
  const [showDisable2FA, setShowDisable2FA] = useState(false);
  
  // Export state
  const [exporting, setExporting] = useState({});
  const [changingPassword, setChangingPassword] = useState(false);

  // Inactivity timeout state
  const [inactivitySettings, setInactivitySettings] = useState({
    agent_away_minutes: 5,
    agent_logout_minutes: 15,
    agent_offline_minutes: 20,
    user_logout_minutes: 5
  });
  const [savingInactivity, setSavingInactivity] = useState(false);

  // Claim Eligibility settings state
  const [eligibilitySettings, setEligibilitySettings] = useState({
    enabled: true,
    months_required: 12,
    allow_early_full_payment: true,
    early_payment_discount_percent: 0,
    grace_period_days: 30
  });
  const [savingEligibility, setSavingEligibility] = useState(false);

  // Payment Reminder settings state
  const [reminderSettings, setReminderSettings] = useState({
    send_7_day: true,
    send_3_day: true,
    send_due_today: true,
    send_overdue: true
  });
  const [savingReminders, setSavingReminders] = useState(false);
  const [triggeringReminders, setTriggeringReminders] = useState(false);
  const [reminderStatus, setReminderStatus] = useState(null);

  // Email Logs state
  const [emailLogs, setEmailLogs] = useState([]);
  const [emailStats, setEmailStats] = useState(null);
  const [loadingLogs, setLoadingLogs] = useState(false);
  const [emailLogFilter, setEmailLogFilter] = useState({ status: "", search: "" });

  // Premium settings state
  const [premiumSettings, setPremiumSettings] = useState({
    premium_multiplier: 1.0,
    base_rate: 0.015
  });
  const [savingPremium, setSavingPremium] = useState(false);
  const [sampleRent, setSampleRent] = useState(2000);

  useEffect(() => {
    fetchSettings();
    fetchInactivitySettings();
    fetchEligibilitySettings();
    fetchReminderSettings();
    fetchPremiumSettings();
  }, []);

  const fetchSettings = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/settings`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSettings(response.data);
      setSubscriptionMode(response.data.subscription_mode || false);
      setRenewalEnabled(response.data.renewal_reminder_enabled ?? true);
      setReminderDays((response.data.renewal_reminder_days || [30, 14, 7, 1]).join(","));
      
      // Set eligibility settings from global settings if present
      if (response.data.claim_eligibility) {
        setEligibilitySettings(response.data.claim_eligibility);
      }
      
      // Fetch 2FA status
      try {
        const twoFaResponse = await axios.get(`${API}/agent/2fa/status`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setTwoFactorEnabled(twoFaResponse.data.two_factor_enabled || false);
      } catch (e) {
        // 2FA status fetch failed, continue
      }
    } catch (error) {
      if (error.response?.status === 403) {
        toast.error("Admin access required for settings");
      } else {
        toast.error("Failed to load settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchInactivitySettings = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/settings/inactivity`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setInactivitySettings({
        agent_away_minutes: response.data.agent_away_minutes || 5,
        agent_logout_minutes: response.data.agent_logout_minutes || 15,
        agent_offline_minutes: response.data.agent_offline_minutes || 20,
        user_logout_minutes: response.data.user_logout_minutes || 5
      });
    } catch (error) {
      // Use defaults if fetch fails
    }
  };

  const fetchEligibilitySettings = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/settings/claim-eligibility`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setEligibilitySettings({
        enabled: response.data.enabled ?? true,
        months_required: response.data.months_required || 12,
        allow_early_full_payment: response.data.allow_early_full_payment ?? true,
        early_payment_discount_percent: response.data.early_payment_discount_percent || 0,
        grace_period_days: response.data.grace_period_days || 30
      });
    } catch (error) {
      // Use defaults if fetch fails
    }
  };

  const fetchReminderSettings = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/settings/payment-reminders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderSettings({
        send_7_day: response.data.send_7_day ?? true,
        send_3_day: response.data.send_3_day ?? true,
        send_due_today: response.data.send_due_today ?? true,
        send_overdue: response.data.send_overdue ?? true
      });
    } catch (error) {
      // Use defaults if fetch fails
    }
    
    // Also get last reminder run status
    try {
      const statusResponse = await axios.get(`${API}/cron/payment-reminders/status`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderStatus(statusResponse.data);
    } catch (error) {
      // Status fetch failed
    }
  };

  const saveReminderSettings = async () => {
    setSavingReminders(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/settings/payment-reminders`, reminderSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment reminder settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save reminder settings");
    } finally {
      setSavingReminders(false);
    }
  };

  const triggerPaymentReminders = async () => {
    setTriggeringReminders(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/cron/payment-reminders`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Payment reminder job triggered successfully");
      // Refresh status after a short delay
      setTimeout(fetchReminderSettings, 3000);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to trigger reminders");
    } finally {
      setTriggeringReminders(false);
    }
  };

  const fetchEmailLogs = async () => {
    setLoadingLogs(true);
    const token = localStorage.getItem("agent_token");
    try {
      const params = new URLSearchParams();
      if (emailLogFilter.status) params.append("status", emailLogFilter.status);
      if (emailLogFilter.search) params.append("search", emailLogFilter.search);
      params.append("limit", "50");
      
      const [logsRes, statsRes] = await Promise.all([
        axios.get(`${API}/agent/email-logs?${params.toString()}`, {
          headers: { Authorization: `Bearer ${token}` }
        }),
        axios.get(`${API}/agent/email-logs/stats`, {
          headers: { Authorization: `Bearer ${token}` }
        })
      ]);
      
      setEmailLogs(logsRes.data.logs || []);
      setEmailStats(statsRes.data);
    } catch (error) {
      toast.error("Failed to load email logs");
    } finally {
      setLoadingLogs(false);
    }
  };

  const saveEligibilitySettings = async () => {
    setSavingEligibility(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/settings/claim-eligibility`, eligibilitySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Claim eligibility settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save eligibility settings");
    } finally {
      setSavingEligibility(false);
    }
  };

  const fetchPremiumSettings = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/settings/premium`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPremiumSettings({
        premium_multiplier: response.data.premium_multiplier || 1.0,
        base_rate: response.data.base_rate || 0.015
      });
    } catch (error) {
      // Premium settings fetch failed, use defaults
    }
  };

  const savePremiumSettings = async () => {
    setSavingPremium(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/settings/premium`, premiumSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Premium pricing settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save premium settings");
    } finally {
      setSavingPremium(false);
    }
  };

  const saveInactivitySettings = async () => {
    setSavingInactivity(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.put(`${API}/agent/settings/inactivity`, inactivitySettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Inactivity timeout settings saved successfully");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save inactivity settings");
    } finally {
      setSavingInactivity(false);
    }
  };

  const start2FASetup = async () => {
    setEnabling2FA(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/2fa/setup`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setQrCodeData(response.data.qr_code);
      setTotpSecret(response.data.secret);
      setShow2FASetup(true);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to setup 2FA");
    } finally {
      setEnabling2FA(false);
    }
  };

  const verify2FASetup = async () => {
    if (verifyCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    setVerifying2FA(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/2fa/verify-setup`, { code: verifyCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFactorEnabled(true);
      setShow2FASetup(false);
      setQrCodeData(null);
      setTotpSecret("");
      setVerifyCode("");
      toast.success("Two-factor authentication enabled successfully!");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid code. Please try again.");
    } finally {
      setVerifying2FA(false);
    }
  };

  const disable2FA = async () => {
    if (disableCode.length !== 6) {
      toast.error("Please enter a 6-digit code");
      return;
    }
    setEnabling2FA(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/2fa/disable`, { code: disableCode }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTwoFactorEnabled(false);
      setShowDisable2FA(false);
      setDisableCode("");
      toast.success("Two-factor authentication disabled");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to disable 2FA");
    } finally {
      setEnabling2FA(false);
    }
  };

  const toggle2FA = async () => {
    if (twoFactorEnabled) {
      setShowDisable2FA(true);
    } else {
      start2FASetup();
    }
  };

  const handleExport = async (type) => {
    setExporting(prev => ({ ...prev, [type]: true }));
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.get(`${API}/agent/export/${type}`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${type}_export_${new Date().toISOString().split('T')[0]}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} exported successfully`);
    } catch (error) {
      toast.error(`Failed to export ${type}`);
    } finally {
      setExporting(prev => ({ ...prev, [type]: false }));
    }
  };

  const testStripeConnection = async () => {
    setTesting(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/settings/test-stripe`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.connected) {
        toast.success(response.data.message);
      } else {
        toast.error(response.data.message);
      }
    } catch (error) {
      toast.error("Failed to test Stripe connection");
    } finally {
      setTesting(false);
    }
  };

  const saveStripeSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem("agent_token");
    try {
      const payload = { subscription_mode: subscriptionMode };
      if (stripeKey) payload.stripe_api_key = stripeKey;
      if (webhookSecret) payload.stripe_webhook_secret = webhookSecret;
      
      await axios.put(`${API}/agent/settings/stripe`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Stripe settings saved successfully");
      setStripeKey("");
      setWebhookSecret("");
      fetchSettings();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to save Stripe settings");
    } finally {
      setSaving(false);
    }
  };

  const saveCronSettings = async () => {
    setSaving(true);
    const token = localStorage.getItem("agent_token");
    try {
      const days = reminderDays.split(",").map(d => parseInt(d.trim())).filter(d => !isNaN(d) && d > 0);
      await axios.put(`${API}/agent/settings/cron`, {
        renewal_reminder_enabled: renewalEnabled,
        renewal_reminder_days: days
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Scheduler settings saved successfully");
    } catch (error) {
      toast.error("Failed to save scheduler settings");
    } finally {
      setSaving(false);
    }
  };

  const triggerRenewalCheck = async () => {
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/cron/trigger-renewal-check`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(response.data.message);
    } catch (error) {
      toast.error("Failed to trigger renewal check");
    }
  };

  const [sendingLoyaltyReminders, setSendingLoyaltyReminders] = useState(false);
  const [issuingLoyaltyRewards, setIssuingLoyaltyRewards] = useState(false);

  const triggerLoyaltyReminders = async () => {
    setSendingLoyaltyReminders(true);
    try {
      const response = await axios.post(`${API}/cron/run/loyalty-progress-reminders`);
      toast.success(`Sent ${response.data.sent_count} loyalty progress reminders (${response.data.skipped_count} skipped)`);
    } catch (error) {
      toast.error("Failed to send loyalty reminders");
    } finally {
      setSendingLoyaltyReminders(false);
    }
  };

  const triggerLoyaltyRewards = async () => {
    setIssuingLoyaltyRewards(true);
    const token = localStorage.getItem("agent_token");
    try {
      const response = await axios.post(`${API}/agent/loyalty/check-and-issue`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success(`Issued ${response.data.issued_count} loyalty rewards (${response.data.skipped_count} skipped)`);
    } catch (error) {
      toast.error("Failed to issue loyalty rewards");
    } finally {
      setIssuingLoyaltyRewards(false);
    }
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error("Passwords don't match");
      return;
    }
    if (passwordForm.new_password.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    setChangingPassword(true);
    const token = localStorage.getItem("agent_token");
    try {
      await axios.post(`${API}/agent/change-password`, {
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Password changed successfully");
      setPasswordForm({ current_password: "", new_password: "", confirm_password: "" });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to change password");
    } finally {
      setChangingPassword(false);
    }
  };

  if (loading) {
    return (
      <AgentLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </AgentLayout>
    );
  }

  return (
    <AgentLayout>
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-foreground" style={{ fontFamily: 'Young Serif, serif' }}>
          <Settings className="inline h-8 w-8 mr-2 text-primary" />
          Settings
        </h1>
        <p className="text-muted-foreground mt-1">
          Configure system settings and integrations
        </p>
      </div>

      <Tabs defaultValue="stripe">
        <TabsList className="mb-6">
          <TabsTrigger value="stripe" data-testid="stripe-tab">
            <CreditCard className="h-4 w-4 mr-2" />
            Stripe
          </TabsTrigger>
          <TabsTrigger value="scheduler" data-testid="scheduler-tab">
            <Clock className="h-4 w-4 mr-2" />
            Scheduler
          </TabsTrigger>
          <TabsTrigger value="inactivity" data-testid="inactivity-tab">
            <Clock className="h-4 w-4 mr-2" />
            Session Timeout
          </TabsTrigger>
          <TabsTrigger value="loyalty" data-testid="loyalty-tab">
            <Gift className="h-4 w-4 mr-2" />
            Loyalty
          </TabsTrigger>
          <TabsTrigger value="security" data-testid="security-tab">
            <NestIcon className="h-4 w-4 mr-2" />
            Security
          </TabsTrigger>
          <TabsTrigger value="exports" data-testid="exports-tab">
            <Download className="h-4 w-4 mr-2" />
            Exports
          </TabsTrigger>
          <TabsTrigger value="password" data-testid="password-tab">
            <Key className="h-4 w-4 mr-2" />
            Password
          </TabsTrigger>
          <TabsTrigger value="eligibility" data-testid="eligibility-tab">
            <Shield className="h-4 w-4 mr-2" />
            Claim Rules
          </TabsTrigger>
          <TabsTrigger value="pricing" data-testid="pricing-tab">
            <Percent className="h-4 w-4 mr-2" />
            Pricing
          </TabsTrigger>
          <TabsTrigger value="reminders" data-testid="reminders-tab">
            <Bell className="h-4 w-4 mr-2" />
            Reminders
          </TabsTrigger>
          <TabsTrigger value="email-logs" data-testid="email-logs-tab">
            <Mail className="h-4 w-4 mr-2" />
            Email Logs
          </TabsTrigger>
        </TabsList>

        {/* Stripe Settings */}
        <TabsContent value="stripe">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <CreditCard className="inline h-5 w-5 mr-2 text-primary" />
                Stripe Integration
              </CardTitle>
              <CardDescription>
                Configure Stripe for payment processing
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Connection Status */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div className="flex items-center gap-3">
                  {settings?.stripe_connected ? (
                    <CheckCircle2 className="h-6 w-6 text-green-600" />
                  ) : (
                    <XCircle className="h-6 w-6 text-red-500" />
                  )}
                  <div>
                    <p className="font-medium">Connection Status</p>
                    <p className="text-sm text-muted-foreground">
                      {settings?.stripe_connected ? (
                        <>Connected • Key: {settings?.stripe_key_masked}</>
                      ) : (
                        "Not connected"
                      )}
                    </p>
                  </div>
                </div>
                <Button
                  variant="outline"
                  onClick={testStripeConnection}
                  disabled={testing}
                  data-testid="test-stripe-btn"
                >
                  {testing ? (
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  ) : (
                    <RefreshCw className="h-4 w-4 mr-2" />
                  )}
                  Test Connection
                </Button>
              </div>

              {/* API Key Input */}
              <div className="space-y-2">
                <Label htmlFor="stripe-key">Stripe API Key (Secret Key)</Label>
                <div className="relative">
                  <Input
                    id="stripe-key"
                    type={showStripeKey ? "text" : "password"}
                    placeholder="sk_test_... or sk_live_..."
                    value={stripeKey}
                    onChange={(e) => setStripeKey(e.target.value)}
                    className="pr-10"
                    data-testid="stripe-key-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowStripeKey(!showStripeKey)}
                  >
                    {showStripeKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Get your API key from the <a href="https://dashboard.stripe.com/apikeys" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">Stripe Dashboard</a>
                </p>
              </div>

              {/* Webhook Secret Input */}
              <div className="space-y-2">
                <Label htmlFor="webhook-secret">Webhook Signing Secret</Label>
                <div className="relative">
                  <Input
                    id="webhook-secret"
                    type={showWebhookSecret ? "text" : "password"}
                    placeholder="whsec_..."
                    value={webhookSecret}
                    onChange={(e) => setWebhookSecret(e.target.value)}
                    className="pr-10"
                    data-testid="webhook-secret-input"
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2"
                    onClick={() => setShowWebhookSecret(!showWebhookSecret)}
                  >
                    {showWebhookSecret ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  Required for subscription webhooks. Get it from your Stripe webhook endpoint settings.
                </p>
              </div>

              {/* Subscription Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Subscription Mode</p>
                  <p className="text-sm text-muted-foreground">
                    Enable recurring monthly billing for policies
                  </p>
                </div>
                <Switch
                  checked={subscriptionMode}
                  onCheckedChange={setSubscriptionMode}
                  data-testid="subscription-mode-toggle"
                />
              </div>

              <Button
                onClick={saveStripeSettings}
                disabled={saving}
                className="w-full"
                data-testid="save-stripe-btn"
              >
                {saving ? "Saving..." : "Save Stripe Settings"}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Scheduler Settings */}
        <TabsContent value="scheduler">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Clock className="inline h-5 w-5 mr-2 text-primary" />
                Renewal Reminder Scheduler
              </CardTitle>
              <CardDescription>
                Configure automated policy renewal reminders
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Enable/Disable Toggle */}
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Enable Renewal Reminders</p>
                  <p className="text-sm text-muted-foreground">
                    Automatically send email reminders before policy expiry
                  </p>
                </div>
                <Switch
                  checked={renewalEnabled}
                  onCheckedChange={setRenewalEnabled}
                  data-testid="renewal-enabled-toggle"
                />
              </div>

              {/* Reminder Days Input */}
              <div className="space-y-2">
                <Label htmlFor="reminder-days">Reminder Days (before expiry)</Label>
                <Input
                  id="reminder-days"
                  placeholder="30, 14, 7, 1"
                  value={reminderDays}
                  onChange={(e) => setReminderDays(e.target.value)}
                  data-testid="reminder-days-input"
                />
                <p className="text-xs text-muted-foreground">
                  Comma-separated list of days before expiry to send reminders (e.g., 30, 14, 7, 1)
                </p>
              </div>

              {/* Current Schedule Display */}
              <div className="p-4 bg-muted/50 rounded-lg">
                <p className="font-medium mb-2">Current Schedule</p>
                <div className="flex flex-wrap gap-2">
                  {(settings?.renewal_reminder_days || [30, 14, 7, 1]).map((day) => (
                    <Badge key={day} variant="secondary">
                      {day} day{day !== 1 ? 's' : ''} before
                    </Badge>
                  ))}
                </div>
              </div>

              <div className="flex gap-3">
                <Button
                  onClick={saveCronSettings}
                  disabled={saving}
                  className="flex-1"
                  data-testid="save-scheduler-btn"
                >
                  {saving ? "Saving..." : "Save Scheduler Settings"}
                </Button>
                <Button
                  onClick={triggerRenewalCheck}
                  variant="outline"
                  data-testid="trigger-renewal-btn"
                >
                  <Send className="h-4 w-4 mr-2" />
                  Trigger Now
                </Button>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                The scheduler runs automatically. Use "Trigger Now" for testing.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Inactivity Timeout Settings */}
        <TabsContent value="inactivity">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Clock className="inline h-5 w-5 mr-2 text-primary" />
                Session Timeout Settings
              </CardTitle>
              <CardDescription>
                Configure automatic logout and status changes due to inactivity
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Admin/Team Settings */}
              <div className="space-y-4">
                <h3 className="font-semibold text-lg">Admin & Team Members</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="agent-away">Mark as Away (minutes)</Label>
                    <Input
                      id="agent-away"
                      type="number"
                      min={1}
                      max={60}
                      value={inactivitySettings.agent_away_minutes}
                      onChange={(e) => setInactivitySettings(prev => ({
                        ...prev,
                        agent_away_minutes: parseInt(e.target.value) || 5
                      }))}
                      data-testid="agent-away-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Time before marking team member as "Away"
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="agent-logout">Auto Logout (minutes)</Label>
                    <Input
                      id="agent-logout"
                      type="number"
                      min={1}
                      max={120}
                      value={inactivitySettings.agent_logout_minutes}
                      onChange={(e) => setInactivitySettings(prev => ({
                        ...prev,
                        agent_logout_minutes: parseInt(e.target.value) || 15
                      }))}
                      data-testid="agent-logout-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Time before automatic logout
                    </p>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="agent-offline">Mark as Offline (minutes)</Label>
                    <Input
                      id="agent-offline"
                      type="number"
                      min={1}
                      max={120}
                      value={inactivitySettings.agent_offline_minutes}
                      onChange={(e) => setInactivitySettings(prev => ({
                        ...prev,
                        agent_offline_minutes: parseInt(e.target.value) || 20
                      }))}
                      data-testid="agent-offline-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Time before marking team member as "Offline"
                    </p>
                  </div>
                </div>
              </div>

              {/* User Settings */}
              <div className="space-y-4 pt-4 border-t">
                <h3 className="font-semibold text-lg">Regular Users</h3>
                <div className="max-w-xs">
                  <div className="space-y-2">
                    <Label htmlFor="user-logout">Auto Logout (minutes)</Label>
                    <Input
                      id="user-logout"
                      type="number"
                      min={1}
                      max={60}
                      value={inactivitySettings.user_logout_minutes}
                      onChange={(e) => setInactivitySettings(prev => ({
                        ...prev,
                        user_logout_minutes: parseInt(e.target.value) || 5
                      }))}
                      data-testid="user-logout-input"
                    />
                    <p className="text-xs text-muted-foreground">
                      Time before automatic logout for regular users
                    </p>
                  </div>
                </div>
              </div>

              {/* Info box */}
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
                <h4 className="font-medium text-blue-800 mb-2">How it works:</h4>
                <ul className="text-sm text-blue-700 space-y-1 list-disc list-inside">
                  <li><strong>Away:</strong> Team member is marked as "Away" (yellow status) after inactivity</li>
                  <li><strong>Logout:</strong> Session expires and user is redirected to login</li>
                  <li><strong>Offline:</strong> Team member status changes to "Offline" (gray status)</li>
                  <li>Activity resets timers: mouse movement, clicks, keyboard, scrolling</li>
                </ul>
              </div>

              <Button 
                onClick={saveInactivitySettings} 
                disabled={savingInactivity}
                className="w-full md:w-auto"
                data-testid="save-inactivity-btn"
              >
                {savingInactivity ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <CheckCircle2 className="h-4 w-4 mr-2" />
                    Save Timeout Settings
                  </>
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Loyalty Settings */}
        <TabsContent value="loyalty">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Gift className="inline h-5 w-5 mr-2 text-primary" />
                Loyalty Reward Program
              </CardTitle>
              <CardDescription>
                Manage loyalty rewards and progress reminders
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Program Info */}
              <div className="p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                <h4 className="font-semibold text-amber-800 mb-2">Program Details</h4>
                <ul className="text-sm text-amber-700 space-y-1">
                  <li>• Users who stay claim-free for <strong>12 months</strong> receive <strong>15%</strong> of premiums paid</li>
                  <li>• Rewards are automatically credited to user's wallet</li>
                  <li>• Users receive thank-you emails and Telegram notifications</li>
                </ul>
              </div>

              {/* Monthly Progress Reminders */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Mail className="h-4 w-4 text-primary" />
                      Monthly Progress Reminders
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Send email reminders to users showing their loyalty progress (e.g., "You're 8/12 months toward your reward!")
                    </p>
                  </div>
                </div>
                <Button
                  onClick={triggerLoyaltyReminders}
                  disabled={sendingLoyaltyReminders}
                  variant="outline"
                  className="w-full mt-4"
                  data-testid="send-loyalty-reminders-btn"
                >
                  {sendingLoyaltyReminders ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Sending...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Send Progress Reminders Now
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Sends to all users with active policies (1-11 months)
                </p>
              </div>

              {/* Issue Rewards */}
              <div className="p-4 bg-muted rounded-lg">
                <div className="flex items-start justify-between">
                  <div>
                    <p className="font-medium flex items-center gap-2">
                      <Gift className="h-4 w-4 text-green-600" />
                      Issue Loyalty Rewards
                    </p>
                    <p className="text-sm text-muted-foreground mt-1">
                      Check all policies and issue rewards to eligible users (12+ months, no claims)
                    </p>
                  </div>
                </div>
                <Button
                  onClick={triggerLoyaltyRewards}
                  disabled={issuingLoyaltyRewards}
                  className="w-full mt-4 bg-green-600 hover:bg-green-700"
                  data-testid="issue-loyalty-rewards-btn"
                >
                  {issuingLoyaltyRewards ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Processing...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Check & Issue Rewards
                    </>
                  )}
                </Button>
                <p className="text-xs text-muted-foreground mt-2 text-center">
                  Rewards are credited to user wallets automatically
                </p>
              </div>

              <p className="text-xs text-center text-muted-foreground">
                Set up an external cron job to run these automatically each month
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Security Settings (2FA) */}
        <TabsContent value="security">
          <Card className="card-organic max-w-lg">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <NestIcon className="inline h-5 w-5 mr-2 text-primary" />
                Two-Factor Authentication
              </CardTitle>
              <CardDescription>
                Add an extra layer of security to your account using an authenticator app
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <p className="font-medium">Authenticator App 2FA</p>
                  <p className="text-sm text-muted-foreground">
                    Use Google Authenticator, Authy, or similar apps
                  </p>
                </div>
                <div className="flex items-center gap-3">
                  <Badge className={twoFactorEnabled ? "bg-green-100 text-green-800" : "bg-gray-100 text-gray-600"}>
                    {twoFactorEnabled ? "Enabled" : "Disabled"}
                  </Badge>
                  {twoFactorEnabled ? (
                    <Button 
                      variant="outline" 
                      size="sm"
                      onClick={() => setShowDisable2FA(true)}
                      disabled={enabling2FA}
                      className="text-red-600 hover:bg-red-50"
                      data-testid="2fa-disable-btn"
                    >
                      Disable
                    </Button>
                  ) : (
                    <Button 
                      size="sm"
                      onClick={start2FASetup}
                      disabled={enabling2FA}
                      data-testid="2fa-setup-btn"
                    >
                      {enabling2FA ? "Setting up..." : "Enable"}
                    </Button>
                  )}
                </div>
              </div>
              
              {twoFactorEnabled && (
                <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                  <h4 className="font-semibold text-green-800 mb-2">How it works</h4>
                  <ol className="text-sm text-green-700 space-y-1 list-decimal list-inside">
                    <li>Enter your email and password on login</li>
                    <li>Open your authenticator app</li>
                    <li>Enter the 6-digit code shown in the app</li>
                    <li>Codes refresh every 30 seconds</li>
                  </ol>
                </div>
              )}
              
              {!twoFactorEnabled && (
                <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
                  <h4 className="font-semibold text-amber-800 mb-2">Recommended</h4>
                  <p className="text-sm text-amber-700">
                    Enable two-factor authentication to protect your account. You'll need an authenticator app like Google Authenticator, Authy, or Microsoft Authenticator.
                  </p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* 2FA Setup Dialog */}
          <Dialog open={show2FASetup} onOpenChange={setShow2FASetup}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Set Up Two-Factor Authentication</DialogTitle>
                <DialogDescription>
                  Scan the QR code with your authenticator app, then enter the verification code
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                {qrCodeData && (
                  <div className="flex justify-center p-4 bg-white rounded-lg">
                    <img src={qrCodeData} alt="2FA QR Code" className="w-48 h-48" data-testid="2fa-qr-code" />
                  </div>
                )}
                
                <div className="text-center">
                  <p className="text-sm text-muted-foreground mb-2">Can't scan? Enter this code manually:</p>
                  <code className="bg-muted px-3 py-1 rounded text-sm font-mono select-all" data-testid="2fa-secret">
                    {totpSecret}
                  </code>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="verify-code">Enter verification code</Label>
                  <Input
                    id="verify-code"
                    type="text"
                    placeholder="000000"
                    value={verifyCode}
                    onChange={(e) => setVerifyCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    data-testid="2fa-verify-input"
                  />
                </div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  setShow2FASetup(false);
                  setQrCodeData(null);
                  setTotpSecret("");
                  setVerifyCode("");
                }}>
                  Cancel
                </Button>
                <Button onClick={verify2FASetup} disabled={verifying2FA || verifyCode.length !== 6} data-testid="2fa-verify-btn">
                  {verifying2FA ? "Verifying..." : "Verify & Enable"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>

          {/* Disable 2FA Dialog */}
          <Dialog open={showDisable2FA} onOpenChange={setShowDisable2FA}>
            <DialogContent className="sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Disable Two-Factor Authentication</DialogTitle>
                <DialogDescription>
                  Enter a code from your authenticator app to confirm
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p className="text-sm text-red-700">
                    <strong>Warning:</strong> Disabling 2FA will make your account less secure. Are you sure you want to continue?
                  </p>
                </div>
                
                <div className="space-y-2">
                  <Label htmlFor="disable-code">Enter verification code</Label>
                  <Input
                    id="disable-code"
                    type="text"
                    placeholder="000000"
                    value={disableCode}
                    onChange={(e) => setDisableCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                    className="text-center text-2xl tracking-widest font-mono"
                    maxLength={6}
                    data-testid="2fa-disable-input"
                  />
                </div>
              </div>
              <DialogFooter className="flex gap-2">
                <Button variant="outline" onClick={() => {
                  setShowDisable2FA(false);
                  setDisableCode("");
                }}>
                  Cancel
                </Button>
                <Button 
                  variant="destructive" 
                  onClick={disable2FA} 
                  disabled={enabling2FA || disableCode.length !== 6}
                  data-testid="2fa-confirm-disable-btn"
                >
                  {enabling2FA ? "Disabling..." : "Disable 2FA"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </TabsContent>

        {/* Data Exports */}
        <TabsContent value="exports">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Download className="inline h-5 w-5 mr-2 text-primary" />
                Data Exports
              </CardTitle>
              <CardDescription>
                Download your data as CSV files
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid md:grid-cols-2 gap-4">
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <FileDown className="h-5 w-5 text-blue-600" />
                    <span className="font-medium">Users Export</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Export all user accounts with contact information
                  </p>
                  <Button 
                    onClick={() => handleExport('users')}
                    disabled={exporting.users}
                    variant="outline"
                    className="w-full"
                    data-testid="export-users-btn"
                  >
                    {exporting.users ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Users
                  </Button>
                </div>
                
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <FileDown className="h-5 w-5 text-green-600" />
                    <span className="font-medium">Quotes Export</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Export all quotes with pricing details
                  </p>
                  <Button 
                    onClick={() => handleExport('quotes')}
                    disabled={exporting.quotes}
                    variant="outline"
                    className="w-full"
                    data-testid="export-quotes-btn"
                  >
                    {exporting.quotes ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Quotes
                  </Button>
                </div>
                
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <FileDown className="h-5 w-5 text-purple-600" />
                    <span className="font-medium">Policies Export</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Export all policies with coverage details
                  </p>
                  <Button 
                    onClick={() => handleExport('policies')}
                    disabled={exporting.policies}
                    variant="outline"
                    className="w-full"
                    data-testid="export-policies-btn"
                  >
                    {exporting.policies ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Policies
                  </Button>
                </div>
                
                <div className="p-4 bg-muted rounded-lg">
                  <div className="flex items-center gap-3 mb-3">
                    <FileDown className="h-5 w-5 text-amber-600" />
                    <span className="font-medium">Claims Export</span>
                  </div>
                  <p className="text-sm text-muted-foreground mb-3">
                    Export all claims with status information
                  </p>
                  <Button 
                    onClick={() => handleExport('claims')}
                    disabled={exporting.claims}
                    variant="outline"
                    className="w-full"
                    data-testid="export-claims-btn"
                  >
                    {exporting.claims ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Download className="h-4 w-4 mr-2" />
                    )}
                    Export Claims
                  </Button>
                </div>
              </div>
              
              <p className="text-xs text-center text-muted-foreground mt-4">
                All exports are downloaded as CSV files compatible with Excel and Google Sheets
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Password Settings */}
        <TabsContent value="password">
          <Card className="card-organic max-w-lg">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Key className="inline h-5 w-5 mr-2 text-primary" />
                Change Password
              </CardTitle>
              <CardDescription>
                Update your account password
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleChangePassword} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="current-password">Current Password</Label>
                  <Input
                    id="current-password"
                    type="password"
                    value={passwordForm.current_password}
                    onChange={(e) => setPasswordForm({...passwordForm, current_password: e.target.value})}
                    required
                    data-testid="current-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="new-password">New Password</Label>
                  <Input
                    id="new-password"
                    type="password"
                    value={passwordForm.new_password}
                    onChange={(e) => setPasswordForm({...passwordForm, new_password: e.target.value})}
                    required
                    minLength={8}
                    data-testid="new-password-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirm-password">Confirm New Password</Label>
                  <Input
                    id="confirm-password"
                    type="password"
                    value={passwordForm.confirm_password}
                    onChange={(e) => setPasswordForm({...passwordForm, confirm_password: e.target.value})}
                    required
                    data-testid="confirm-password-input"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={changingPassword}
                  className="w-full"
                  data-testid="change-password-btn"
                >
                  {changingPassword ? "Changing..." : "Change Password"}
                </Button>
              </form>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Claim Eligibility Settings */}
        <TabsContent value="eligibility">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Shield className="inline h-5 w-5 mr-2 text-primary" />
                Claim Eligibility Rules
              </CardTitle>
              <CardDescription>
                Configure when customers become eligible to file claims
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Main toggle */}
              <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                <div>
                  <Label className="text-base font-medium">Enable Eligibility Requirements</Label>
                  <p className="text-sm text-muted-foreground mt-1">
                    When disabled, all policyholders can file claims immediately
                  </p>
                </div>
                <Switch
                  checked={eligibilitySettings.enabled}
                  onCheckedChange={(v) => setEligibilitySettings(prev => ({ ...prev, enabled: v }))}
                  data-testid="eligibility-enabled-toggle"
                />
              </div>

              {eligibilitySettings.enabled && (
                <>
                  {/* Months Required */}
                  <div className="grid grid-cols-2 gap-6">
                    <div className="space-y-2">
                      <Label>Months Required for Eligibility</Label>
                      <Input
                        type="number"
                        min="1"
                        max="24"
                        value={eligibilitySettings.months_required}
                        onChange={(e) => setEligibilitySettings(prev => ({ 
                          ...prev, 
                          months_required: parseInt(e.target.value) || 12 
                        }))}
                        data-testid="months-required-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Number of premium payments required before claim eligibility
                      </p>
                    </div>

                    <div className="space-y-2">
                      <Label>Grace Period (Days)</Label>
                      <Input
                        type="number"
                        min="0"
                        max="90"
                        value={eligibilitySettings.grace_period_days}
                        onChange={(e) => setEligibilitySettings(prev => ({ 
                          ...prev, 
                          grace_period_days: parseInt(e.target.value) || 30 
                        }))}
                        data-testid="grace-period-input"
                      />
                      <p className="text-xs text-muted-foreground">
                        Days after missed payment before losing eligibility
                      </p>
                    </div>
                  </div>

                  {/* Early Full Payment Option */}
                  <div className="border rounded-lg p-4 space-y-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <Label className="text-base font-medium flex items-center gap-2">
                          <DollarSign className="h-4 w-4" />
                          Allow Early Full Payment
                        </Label>
                        <p className="text-sm text-muted-foreground mt-1">
                          Users can pay full 12-month premium at once to become eligible immediately
                        </p>
                      </div>
                      <Switch
                        checked={eligibilitySettings.allow_early_full_payment}
                        onCheckedChange={(v) => setEligibilitySettings(prev => ({ 
                          ...prev, 
                          allow_early_full_payment: v 
                        }))}
                        data-testid="early-payment-toggle"
                      />
                    </div>

                    {eligibilitySettings.allow_early_full_payment && (
                      <div className="space-y-2 pt-2 border-t">
                        <Label>Early Payment Discount (%)</Label>
                        <Input
                          type="number"
                          min="0"
                          max="50"
                          step="0.5"
                          value={eligibilitySettings.early_payment_discount_percent}
                          onChange={(e) => setEligibilitySettings(prev => ({ 
                            ...prev, 
                            early_payment_discount_percent: parseFloat(e.target.value) || 0 
                          }))}
                          data-testid="discount-percent-input"
                        />
                        <p className="text-xs text-muted-foreground">
                          Discount to offer when paying the full annual premium upfront (0 = no discount)
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Summary */}
                  <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                    <h4 className="font-medium text-primary mb-2">Current Configuration</h4>
                    <ul className="text-sm space-y-1 text-muted-foreground">
                      <li>• Users must make <strong>{eligibilitySettings.months_required}</strong> monthly payments to become eligible</li>
                      <li>• Grace period of <strong>{eligibilitySettings.grace_period_days}</strong> days after missed payment</li>
                      {eligibilitySettings.allow_early_full_payment && (
                        <>
                          <li>• Users can pay full 12-month premium to become eligible immediately</li>
                          {eligibilitySettings.early_payment_discount_percent > 0 && (
                            <li>• <strong>{eligibilitySettings.early_payment_discount_percent}%</strong> discount for paying annual premium upfront</li>
                          )}
                        </>
                      )}
                    </ul>
                  </div>
                </>
              )}

              <Button 
                onClick={saveEligibilitySettings} 
                disabled={savingEligibility}
                className="w-full sm:w-auto"
                data-testid="save-eligibility-btn"
              >
                {savingEligibility ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Claim Rules"
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Pricing Settings */}
        <TabsContent value="pricing">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Percent className="inline h-5 w-5 mr-2 text-primary" />
                Premium Pricing Configuration
              </CardTitle>
              <CardDescription>
                Configure the premium multiplier to adjust pricing across all quotes and policies
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Premium Multiplier */}
              <div className="space-y-4">
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="text-base font-medium">Premium Multiplier</Label>
                    <span className="text-2xl font-bold text-primary">
                      {(premiumSettings.premium_multiplier * 100).toFixed(0)}%
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Adjust the multiplier to increase or decrease premium prices. 100% = standard pricing.
                  </p>
                  <input
                    type="range"
                    min="10"
                    max="500"
                    value={premiumSettings.premium_multiplier * 100}
                    onChange={(e) => setPremiumSettings(prev => ({
                      ...prev,
                      premium_multiplier: parseFloat(e.target.value) / 100
                    }))}
                    className="w-full h-2 bg-muted rounded-lg appearance-none cursor-pointer accent-primary"
                    data-testid="premium-multiplier-slider"
                  />
                  <div className="flex justify-between text-xs text-muted-foreground">
                    <span>10%</span>
                    <span>100%</span>
                    <span>500%</span>
                  </div>
                </div>

                {/* Direct input for precise control */}
                <div className="flex items-center gap-4">
                  <div className="flex-1 space-y-2">
                    <Label>Or enter exact multiplier</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0.1"
                        max="5.0"
                        step="0.01"
                        value={premiumSettings.premium_multiplier}
                        onChange={(e) => setPremiumSettings(prev => ({
                          ...prev,
                          premium_multiplier: Math.min(5, Math.max(0.1, parseFloat(e.target.value) || 1))
                        }))}
                        className="w-32"
                        data-testid="premium-multiplier-input"
                      />
                      <span className="text-muted-foreground">x</span>
                    </div>
                  </div>
                  <div className="flex-1 space-y-2">
                    <Label>Base Rate (%)</Label>
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        min="0.1"
                        max="10"
                        step="0.1"
                        value={(premiumSettings.base_rate * 100).toFixed(1)}
                        onChange={(e) => setPremiumSettings(prev => ({
                          ...prev,
                          base_rate: Math.min(0.1, Math.max(0.001, parseFloat(e.target.value) / 100 || 0.015))
                        }))}
                        className="w-32"
                        data-testid="base-rate-input"
                      />
                      <span className="text-muted-foreground">% of rent</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Preview Calculator */}
              <div className="border rounded-lg p-4 bg-muted/30">
                <h4 className="font-medium mb-4 flex items-center gap-2">
                  <DollarSign className="h-4 w-4" />
                  Premium Preview Calculator
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Sample Monthly Rent</Label>
                    <Input
                      type="number"
                      value={sampleRent}
                      onChange={(e) => setSampleRent(parseFloat(e.target.value) || 0)}
                      placeholder="Enter monthly rent"
                      data-testid="sample-rent-input"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Calculated Monthly Premium</Label>
                    <div className="h-10 px-3 flex items-center bg-primary/10 rounded-md border border-primary/20">
                      <span className="text-xl font-bold text-primary">
                        ${(sampleRent * premiumSettings.base_rate * premiumSettings.premium_multiplier).toFixed(2)}
                      </span>
                      <span className="text-sm text-muted-foreground ml-2">/ month</span>
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Formula: ${sampleRent} × {(premiumSettings.base_rate * 100).toFixed(1)}% × {premiumSettings.premium_multiplier}x
                    </p>
                  </div>
                </div>
              </div>

              {/* Summary box */}
              <div className="bg-primary/5 rounded-lg p-4 border border-primary/20">
                <h4 className="font-medium text-primary mb-2">Current Pricing Configuration</h4>
                <ul className="text-sm space-y-1 text-muted-foreground">
                  <li>• Base rate: <strong>{(premiumSettings.base_rate * 100).toFixed(2)}%</strong> of monthly rent</li>
                  <li>• Multiplier: <strong>{premiumSettings.premium_multiplier}x</strong> ({(premiumSettings.premium_multiplier * 100).toFixed(0)}%)</li>
                  <li>• Effective rate: <strong>{(premiumSettings.base_rate * premiumSettings.premium_multiplier * 100).toFixed(2)}%</strong> of monthly rent</li>
                </ul>
              </div>

              <Button 
                onClick={savePremiumSettings} 
                disabled={savingPremium}
                className="w-full sm:w-auto"
                data-testid="save-premium-btn"
              >
                {savingPremium ? (
                  <>
                    <RefreshCw className="mr-2 h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Pricing Settings"
                )}
              </Button>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Payment Reminders Settings */}
        <TabsContent value="reminders">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Bell className="inline h-5 w-5 mr-2 text-primary" />
                Payment Reminder Settings
              </CardTitle>
              <CardDescription>
                Configure automatic email reminders for upcoming and overdue premium payments
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Reminder Status */}
              {reminderStatus && (
                <div className="bg-muted/50 p-4 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-sm font-medium">Last Run Status</span>
                    <Badge variant={reminderStatus.is_running ? "default" : "secondary"}>
                      {reminderStatus.is_running ? "Running" : "Idle"}
                    </Badge>
                  </div>
                  {reminderStatus.last_run && (
                    <div className="text-sm text-muted-foreground space-y-1">
                      <p>Last run: {new Date(reminderStatus.last_run.run_at).toLocaleString()}</p>
                      {reminderStatus.last_run.results && (
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mt-2">
                          <div className="bg-blue-50 dark:bg-blue-900/20 p-2 rounded text-center">
                            <p className="text-lg font-bold text-blue-600">{reminderStatus.last_run.results?.["7_day_reminders"] || 0}</p>
                            <p className="text-xs">7-Day</p>
                          </div>
                          <div className="bg-yellow-50 dark:bg-yellow-900/20 p-2 rounded text-center">
                            <p className="text-lg font-bold text-yellow-600">{reminderStatus.last_run.results?.["3_day_reminders"] || 0}</p>
                            <p className="text-xs">3-Day</p>
                          </div>
                          <div className="bg-orange-50 dark:bg-orange-900/20 p-2 rounded text-center">
                            <p className="text-lg font-bold text-orange-600">{reminderStatus.last_run.results?.due_today_reminders || 0}</p>
                            <p className="text-xs">Due Today</p>
                          </div>
                          <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded text-center">
                            <p className="text-lg font-bold text-red-600">{reminderStatus.last_run.results?.overdue_reminders || 0}</p>
                            <p className="text-xs">Overdue</p>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* Reminder Settings */}
              <div className="space-y-4">
                <h4 className="font-medium">Reminder Schedule</h4>
                
                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">7-Day Reminder</p>
                    <p className="text-sm text-muted-foreground">Send reminder 7 days before payment due</p>
                  </div>
                  <Switch
                    checked={reminderSettings.send_7_day}
                    onCheckedChange={(checked) => setReminderSettings({...reminderSettings, send_7_day: checked})}
                    data-testid="reminder-7-day-switch"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">3-Day Reminder</p>
                    <p className="text-sm text-muted-foreground">Send reminder 3 days before payment due</p>
                  </div>
                  <Switch
                    checked={reminderSettings.send_3_day}
                    onCheckedChange={(checked) => setReminderSettings({...reminderSettings, send_3_day: checked})}
                    data-testid="reminder-3-day-switch"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Due Today Reminder</p>
                    <p className="text-sm text-muted-foreground">Send reminder on the day payment is due</p>
                  </div>
                  <Switch
                    checked={reminderSettings.send_due_today}
                    onCheckedChange={(checked) => setReminderSettings({...reminderSettings, send_due_today: checked})}
                    data-testid="reminder-due-today-switch"
                  />
                </div>

                <div className="flex items-center justify-between p-4 border rounded-lg">
                  <div>
                    <p className="font-medium">Overdue Reminder</p>
                    <p className="text-sm text-muted-foreground">Send reminder 1 day after payment was due</p>
                  </div>
                  <Switch
                    checked={reminderSettings.send_overdue}
                    onCheckedChange={(checked) => setReminderSettings({...reminderSettings, send_overdue: checked})}
                    data-testid="reminder-overdue-switch"
                  />
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-3 pt-4">
                <Button 
                  onClick={saveReminderSettings} 
                  disabled={savingReminders}
                  data-testid="save-reminder-settings-btn"
                >
                  {savingReminders ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <CheckCircle2 className="h-4 w-4 mr-2" />
                      Save Settings
                    </>
                  )}
                </Button>
                
                <Button 
                  variant="outline"
                  onClick={triggerPaymentReminders} 
                  disabled={triggeringReminders}
                  data-testid="trigger-reminders-btn"
                >
                  {triggeringReminders ? (
                    <>
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                      Running...
                    </>
                  ) : (
                    <>
                      <Send className="h-4 w-4 mr-2" />
                      Run Reminders Now
                    </>
                  )}
                </Button>
              </div>

              <p className="text-sm text-muted-foreground">
                Note: Payment reminders are automatically sent daily. Use "Run Reminders Now" to manually trigger the reminder job.
              </p>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Email Logs Tab */}
        <TabsContent value="email-logs">
          <Card className="card-organic">
            <CardHeader>
              <CardTitle style={{ fontFamily: 'Young Serif, serif' }}>
                <Mail className="inline h-5 w-5 mr-2 text-primary" />
                Email Logs
              </CardTitle>
              <CardDescription>
                View and monitor all system emails sent to users
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Load Button */}
              <Button 
                onClick={fetchEmailLogs} 
                disabled={loadingLogs}
                data-testid="load-email-logs-btn"
              >
                {loadingLogs ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Loading...
                  </>
                ) : (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2" />
                    Load Email Logs
                  </>
                )}
              </Button>

              {/* Email Stats */}
              {emailStats && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-green-600">{emailStats.total?.sent || 0}</p>
                    <p className="text-sm text-muted-foreground">Sent</p>
                  </div>
                  <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-red-600">{emailStats.total?.failed || 0}</p>
                    <p className="text-sm text-muted-foreground">Failed</p>
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-blue-600">{emailStats.period?.today || 0}</p>
                    <p className="text-sm text-muted-foreground">Today</p>
                  </div>
                  <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg text-center">
                    <p className="text-2xl font-bold text-purple-600">{emailStats.period?.this_month || 0}</p>
                    <p className="text-sm text-muted-foreground">This Month</p>
                  </div>
                </div>
              )}

              {/* Filters */}
              <div className="flex gap-4 items-center">
                <select
                  value={emailLogFilter.status}
                  onChange={(e) => setEmailLogFilter({...emailLogFilter, status: e.target.value})}
                  className="border rounded-md px-3 py-2 bg-background"
                >
                  <option value="">All Status</option>
                  <option value="sent">Sent</option>
                  <option value="failed">Failed</option>
                  <option value="skipped">Skipped</option>
                </select>
                <Input
                  placeholder="Search by email or subject..."
                  value={emailLogFilter.search}
                  onChange={(e) => setEmailLogFilter({...emailLogFilter, search: e.target.value})}
                  className="max-w-xs"
                />
                <Button variant="outline" onClick={fetchEmailLogs} size="sm">
                  Apply
                </Button>
              </div>

              {/* Email Logs Table */}
              {emailLogs.length > 0 ? (
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-muted">
                      <tr>
                        <th className="text-left p-3 font-medium">Date</th>
                        <th className="text-left p-3 font-medium">Recipient</th>
                        <th className="text-left p-3 font-medium">Subject</th>
                        <th className="text-left p-3 font-medium">Type</th>
                        <th className="text-left p-3 font-medium">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {emailLogs.map((log, idx) => (
                        <tr key={log.log_id || idx} className="border-t hover:bg-muted/50">
                          <td className="p-3 text-muted-foreground">
                            {log.created_at ? new Date(log.created_at).toLocaleDateString() : 'N/A'}
                          </td>
                          <td className="p-3">{log.recipient_email}</td>
                          <td className="p-3 max-w-xs truncate" title={log.subject}>
                            {log.subject}
                          </td>
                          <td className="p-3">
                            <Badge variant="outline" className="text-xs">
                              {log.email_type || 'general'}
                            </Badge>
                          </td>
                          <td className="p-3">
                            <Badge 
                              className={
                                log.status === 'sent' ? 'bg-green-100 text-green-700' :
                                log.status === 'failed' ? 'bg-red-100 text-red-700' :
                                'bg-yellow-100 text-yellow-700'
                              }
                            >
                              {log.status}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  <Mail className="h-12 w-12 mx-auto mb-4 opacity-50" />
                  <p>No email logs found</p>
                  <p className="text-sm mt-2">Click "Load Email Logs" to view recent emails</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </AgentLayout>
  );
}
